import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Handshake, Target, Users, Rocket, MapPin, Calendar, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { getSponsors } from '../services/api';

// Lazy-loaded Calendly component
const CalendlyEmbed = ({ url }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);
  const scriptLoadedRef = useRef(false);

  // Use Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' } // Start loading 200px before it's visible
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Load Calendly script only when visible
  useEffect(() => {
    if (!isVisible || scriptLoadedRef.current) return;

    // Check if script already exists
    const existingScript = document.querySelector('script[src*="calendly.com/assets/external/widget.js"]');
    if (existingScript) {
      scriptLoadedRef.current = true;
      // Use setTimeout to avoid setState in effect body warning
      setTimeout(() => setIsLoaded(true), 0);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    script.onload = () => {
      scriptLoadedRef.current = true;
      setIsLoaded(true);
    };
    document.body.appendChild(script);
  }, [isVisible]);

  return (
    <div ref={containerRef} className="relative">
      {/* Loading placeholder */}
      {(!isVisible || !isLoaded) && (
        <div className="flex flex-col items-center justify-center py-20 bg-slate-900/30 rounded-xl border border-slate-800">
          <div className="w-16 h-16 bg-blue-500/10 rounded-full flex items-center justify-center mb-4">
            <Calendar className="w-8 h-8 text-blue-400" />
          </div>
          <div className="flex items-center gap-2 text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Loading calendar...</span>
          </div>
          <p className="text-slate-500 text-sm mt-2">Please wait while we load the scheduling widget</p>
        </div>
      )}
      
      {/* Calendly widget - only render when visible */}
      {isVisible && (
        <div 
          className={`calendly-inline-widget transition-opacity duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0 absolute inset-0'}`}
          data-url={url}
          style={{ minWidth: '320px', height: '700px' }}
        />
      )}
    </div>
  );
};

const Sponsors = () => {
  const navigate = useNavigate();
  const [sponsors, setSponsors] = useState([]);

  const loadSponsors = useCallback(async () => {
    try {
      const data = await getSponsors();
      setSponsors(data);
    } catch (error) {
      console.error('Error loading sponsors:', error);
    }
  }, []);

  useEffect(() => {
    loadSponsors();
  }, [loadSponsors]);

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header - Same as main page */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/90 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <a href="/" className="flex items-center gap-3 hover:opacity-90 transition-opacity">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
              <Rocket className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-white">The Founder Initiative</span>
          </a>
          <nav className="hidden md:flex gap-8 text-sm font-medium text-slate-400">
            <a href="/buyers" className="hover:text-white transition-colors">Buyers</a>
            <a href="/podcast" className="hover:text-white transition-colors">Podcast</a>
            <a href="/sponsors" className="text-white transition-colors">Sponsors</a>
          </nav>
          <a 
            href="https://app.thefounderinitiative.com" 
            className="inline-flex items-center justify-center rounded-md text-sm font-medium bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white border-0 h-10 px-4 py-2"
          >
            Login
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 rounded-full px-4 py-2">
              <Handshake className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-blue-400 font-medium">Partnership Opportunities</span>
            </div>
            <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 rounded-full px-4 py-2">
              <MapPin className="w-4 h-4 text-orange-400" />
              <span className="text-sm text-orange-400 font-medium">San Francisco, CA</span>
            </div>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            Partner With Us to{' '}
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              Empower Founders
            </span>
          </h1>
          
          <p className="text-xl text-slate-400 leading-relaxed max-w-3xl mx-auto">
            Based in the heart of San Francisco, we're on a mission to help founders hit PMF and build durable growth. 
            We only work with partners that share this vision and can enable founders in unique ways.
          </p>
        </div>
      </section>

      {/* Value Props */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Target className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Access to Top Founders</h3>
                <p className="text-slate-400 text-sm">
                  Connect with vetted founders from top accelerators and VC portfolios actively building their next big thing.
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-cyan-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Users className="w-6 h-6 text-cyan-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Brand Visibility</h3>
                <p className="text-slate-400 text-sm">
                  Get your brand in front of founders, enterprise buyers, and top VCs at our exclusive pitch events.
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Rocket className="w-6 h-6 text-purple-400" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">Enable Innovation</h3>
                <p className="text-slate-400 text-sm">
                  Help the next generation of startups succeed by providing tools, resources, and support they need.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Current Sponsors */}
      {sponsors.length > 0 && (
        <section className="py-16 px-6 bg-slate-900/30 border-y border-slate-800">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-white text-center mb-8">Our Partners</h2>
            <div className="flex flex-wrap justify-center gap-8">
              {sponsors.map((sponsor) => (
                <div 
                  key={sponsor._id} 
                  className="bg-slate-900/80 border border-slate-800 rounded-xl px-8 py-6 hover:border-slate-700 transition-all"
                >
                  {sponsor.logo_url ? (
                    <img 
                      src={sponsor.logo_url} 
                      alt={sponsor.name} 
                      className="h-8 object-contain"
                    />
                  ) : (
                    <div className="text-center">
                      <div className="text-xl font-bold text-white">{sponsor.name}</div>
                      {sponsor.subtitle && (
                        <div className="text-xs text-slate-400 mt-1">{sponsor.subtitle}</div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA + Calendly Section */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              Let's Discuss Partnership Opportunities
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              If you align with our mission and you're interested in considering a partnership, 
              please schedule a time to learn more about our sponsorship packages.
            </p>
          </div>
          
          {/* Lazy-loaded Calendly Embed */}
          <div className="bg-slate-900/50 border border-slate-800 rounded-2xl overflow-hidden">
            <CalendlyEmbed url="https://calendly.com/adam-odonnell/1-1-meeting-with-adam-o-donnell-clone" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-slate-800">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} The Founder Initiative. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Sponsors;
